import React, { useId } from "react";
import { useNavigate } from "react-router-dom";
import Modal from "../../../../shared/ui/Modal";
import { useTranslation, getPlatformById } from "../../../../i18n";
import { GoogleBusinessLogo, JamedaLogo, KununuLogo } from "../../platformCatalog";

/**
 * Plan-limit messaging for platform connections (Basic / Premium = one
 * connected profile per platform). Two surfaces share the same copy:
 *
 *   <PlanLimitDialog>  — modal shown when the user tries to add a second
 *                        profile on a platform that is already at its limit.
 *   <PlanLimitBanner>  — inline notice for an OVER-limit platform (legacy
 *                        data / trial that converted): choose what to keep.
 */

const PlatformLogo = ({ id, className = "h-7 w-7" }) => {
  if (id === "google") return <GoogleBusinessLogo className={className} />;
  if (id === "jameda") return <JamedaLogo className={className} />;
  if (id === "kununu") return <KununuLogo className={className} />;
  return null;
};

const SupportIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M4 12a8 8 0 1116 0v4a2 2 0 01-2 2h-1v-6h3M4 12v4a2 2 0 002 2h1v-6H4"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** Navigate to the support page with a pre-filled Enterprise/Custom request. */
export function useRequestMoreProfiles() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  return (platformId) => {
    const platformName = getPlatformById(platformId, t)?.title ?? platformId;
    navigate("/dashboard/client/support", {
      state: {
        presetCategory: "support",
        presetSubject: t("platforms.limit.supportSubject"),
        presetMessage: t("platforms.limit.supportMessage", { platform: platformName }),
      },
    });
  };
}

/** Slot indicator: "1 / 1" with a filled pill — reads at a glance. */
const SlotMeter = ({ used, limit }) => {
  const total = Math.max(limit || 1, used || 0);
  return (
    <div className="flex items-center gap-1.5" aria-hidden>
      {Array.from({ length: total }).map((_, i) => (
        <span
          key={i}
          className={[
            "h-2.5 w-8 rounded-full transition-colors",
            i < used ? "bg-[#006666]" : "bg-[#e2e8f0]",
          ].join(" ")}
        />
      ))}
    </div>
  );
};

export const PlanLimitDialog = ({
  open,
  platformId,
  limit = 1,
  connectedCount = 1,
  onClose,
  onManageCurrent,
}) => {
  const { t } = useTranslation();
  const titleId = useId();
  const requestMore = useRequestMoreProfiles();
  const platformName = getPlatformById(platformId, t)?.title ?? platformId;

  return (
    <Modal open={open} onClose={onClose} labelledBy={titleId}>
      <div className="px-8 pb-8 pt-9 md:px-10">
        <div className="flex items-start gap-4">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-[14px] border border-[#e8ecf0] bg-white shadow-[0_1px_3px_rgba(15,23,42,0.06)]">
            <PlatformLogo id={platformId} className="h-8 w-8" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-[#006666]">
              {t("platforms.limit.eyebrow")}
            </p>
            <h2
              id={titleId}
              className="mt-1 text-[22px] font-bold leading-tight tracking-tight text-[#0f172a]"
            >
              {t("platforms.limit.title")}
            </h2>
          </div>
        </div>

        <div className="mt-6 rounded-[14px] border border-[#e8ecf0] bg-[#f8fafc] px-5 py-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <PlatformLogo id={platformId} className="h-5 w-5" />
              <span className="text-[14px] font-semibold text-[#0f172a]">{platformName}</span>
            </div>
            <div className="flex items-center gap-3">
              <SlotMeter used={Math.min(connectedCount, limit)} limit={limit} />
              <span className="text-[12px] font-semibold tabular-nums text-[#64748b]">
                {t("platforms.limit.slotUsed", {
                  used: Math.min(connectedCount, limit),
                  limit,
                })}
              </span>
            </div>
          </div>
        </div>

        <p className="mt-5 text-[14.5px] leading-[1.65] text-[#334155]">
          {t("platforms.limit.message")}
        </p>

        <div className="mt-7 flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            className="rounded-full px-4 py-2.5 text-[13.5px] font-medium text-[#64748b] transition hover:text-[#0f172a]"
          >
            {t("common.close")}
          </button>
          {onManageCurrent ? (
            <button
              type="button"
              onClick={() => {
                onClose?.();
                onManageCurrent();
              }}
              className="rounded-full border border-[#e2e8f0] bg-white px-5 py-2.5 text-[13.5px] font-semibold text-[#0f172a] transition hover:border-[#cbd5e1] hover:bg-[#f8fafc]"
            >
              {t("platforms.limit.manageCurrent")}
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => {
              onClose?.();
              requestMore(platformId);
            }}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-[#006666] px-6 py-2.5 text-[13.5px] font-bold text-white shadow-[0_4px_14px_rgba(0,102,102,0.3)] transition hover:bg-[#00504a]"
          >
            <SupportIcon />
            {t("platforms.limit.contactSupport")}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export const PlanLimitBanner = ({ platformId, connectedCount, limit = 1, onChoose }) => {
  const { t } = useTranslation();
  const requestMore = useRequestMoreProfiles();
  const platformName = getPlatformById(platformId, t)?.title ?? platformId;

  return (
    <div
      role="status"
      className="relative overflow-hidden rounded-[16px] border border-amber-200/80 bg-gradient-to-r from-[#fffbeb] to-white px-5 py-4 shadow-[0_1px_3px_rgba(15,23,42,0.05)]"
    >
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex min-w-0 items-start gap-3.5">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-white ring-1 ring-amber-200">
            <PlatformLogo id={platformId} className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <p className="text-[14px] font-bold text-[#0f172a]">
              {t("platforms.limit.overLimitTitle")}
            </p>
            <p className="mt-0.5 text-[13px] leading-relaxed text-[#475569]">
              {t("platforms.limit.overLimitBody", {
                platform: platformName,
                count: connectedCount,
                limit,
              })}
            </p>
            <p className="mt-1.5 text-[12.5px] leading-relaxed text-[#64748b]">
              {t("platforms.limit.message")}
            </p>
          </div>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2 md:justify-end">
          <button
            type="button"
            onClick={() => requestMore(platformId)}
            className="rounded-full px-4 py-2 text-[12.5px] font-semibold text-[#006666] transition hover:bg-[#f0fafa]"
          >
            {t("platforms.limit.contactSupport")}
          </button>
          {onChoose ? (
            <button
              type="button"
              onClick={onChoose}
              className="rounded-full bg-[#0f172a] px-5 py-2 text-[12.5px] font-bold text-white shadow-sm transition hover:bg-[#1e293b]"
            >
              {t("platforms.limit.overLimitCta")} →
            </button>
          ) : (
            <span className="max-w-[260px] text-[12px] text-[#64748b]">
              {t("platforms.limit.overLimitJamedaHint")}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export const PlanLimitSidebarCard = ({ platformLimits }) => {
  const { t } = useTranslation();
  const requestMore = useRequestMoreProfiles();
  const entries = Object.entries(platformLimits || {}).filter(
    ([, s]) => s && s.limit != null
  );
  if (entries.length === 0) return null;

  return (
    <div className="rounded-[16px] border border-[#e8ecf0] bg-white p-5 shadow-[0_1px_3px_rgba(15,23,42,0.06)]">
      <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#9ca3af]">
        {t("platforms.limit.sidebarTitle")}
      </p>
      <p className="mt-2 text-[13px] font-semibold text-[#0f172a]">
        {t("platforms.limit.sidebarBody")}
      </p>
      <ul className="mt-4 space-y-3">
        {entries
          .filter(([id]) => id !== "kununu")
          .map(([id, s]) => (
            <li key={id} className="flex items-center justify-between gap-3">
              <span className="flex items-center gap-2 text-[13px] text-[#334155]">
                <PlatformLogo id={id} className="h-4 w-4" />
                {getPlatformById(id, t)?.title ?? id}
              </span>
              <span className="flex items-center gap-2">
                <SlotMeter used={Math.min(s.connectedCount, s.limit)} limit={s.limit} />
                <span
                  className={[
                    "text-[12px] font-semibold tabular-nums",
                    s.overLimit ? "text-amber-700" : "text-[#64748b]",
                  ].join(" ")}
                >
                  {s.connectedCount}/{s.limit}
                </span>
              </span>
            </li>
          ))}
      </ul>
      <button
        type="button"
        onClick={() => requestMore("google")}
        className="mt-4 text-[12.5px] font-semibold text-[#006666] hover:underline"
      >
        {t("platforms.limit.sidebarCta")} →
      </button>
    </div>
  );
};
